import { Component, OnDestroy, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import { Store, select } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, EMPTY, Observable, Subscription } from "rxjs";
import { catchError, finalize, map, tap } from "rxjs/operators";
import { MenuDTO, RouteData } from "src/app/models/Menu/MenuDTO";
import { CurrentAccountDTO } from "src/app/models/current-accounts/CurrentAccountDTO";

import {
  ColumnSorterInfo,
  ConfigScope,
  ConfigSettingType,
  DefaultPage,
  ExportEvent,
  FieldType,
  FilterEvent,
  FormatType,
  PageEvent,
  SearchEvent,
  SortEvent,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { CurrentAccountsService } from "src/app/shared/services/current-accounts/current-accounts.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";
import { subMenusIterator } from "src/app/shared/utils/menuUtils";
import { SnackBarUtils } from "src/app/shared/utils/snackbarUtils";
import { setSnackbar } from "src/app/store/actions/messages/message.actions";
import { AppState, loadedMenusState } from "src/app/store/reducers";
import { CHIP_STYLE, getChipStyle } from "src/app/shared/utils/customTableUtils";

const USAGE_CODE = "CURRENT_ACCOUNTS";

@Component({
  selector: "app-current-accounts-list",
  templateUrl: "./current-accounts-list.component.html",
  styleUrls: ["./current-accounts-list.component.scss"],
})
export class CurrentAccountsListComponent implements OnInit, OnDestroy {
  public isLoading$ = new BehaviorSubject<boolean>(false);
  public totalCurrentAccounts$ = new BehaviorSubject<number>(0);
  public tableTitle: string;
  public headerIcon: string;
  public formPath: string = "current-accounts/current-account";

  public columnInfo$: Observable<ColumnSorterInfo[]>;
  public excelCurrentAccountsList$: Observable<CurrentAccountDTO[]>;

  public tableData: CurrentAccountDTO[];

  private getCurrentAccountsListSub: Subscription;

  private menus$: Observable<MenuDTO[]>;
  private menuSub: Subscription;

  public locale: string = localStorage.getItem("locale");

  public tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "CurrentAccounts.partyCode",
      field: "party.code",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "CurrentAccounts.partyName",
      field: "party.name",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "CurrentAccounts.partyVatin",
      field: "party.vatin",
      fieldType: FieldType.STRING,
    },
    {
      header: "CurrentAccounts.limitCreditParty",
      field: "creditLimit",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.AMOUNT,
      style: "font-weight: 600"
    },
    {
      header: "CurrentAccounts.amount",
      field: "amount",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.AMOUNT,
      style: "font-weight: 600"
    },
    {
      header: "CurrentAccounts.totalDocNotIssuedBalance",
      field: "documentsNotIssuedBalance",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.AMOUNT,
      style: "font-weight: 600"
    },
    {
      header: "CurrentAccounts.availableCredit",
      field: "availableCredit",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.AMOUNT,
      style: "font-weight: 600"
    },
    {
      header: "CurrentAccounts.token",
      field: "token",
      fieldType: FieldType.STRING,
      identifier: true,
      hidden: true,
    },
    {
      header: "CurrentAccounts.amountCredit",
      field: "amountCredit",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.AMOUNT,
      style: "font-weight: 600"
    },
    {
      header: "CurrentAccounts.amountDebit",
      field: "amountDebit",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.AMOUNT,
      style: "font-weight: 600"
    },
    {
      header: "CurrentAccounts.partyRole",
      field: "partyRole.description",
      fieldType: FieldType.CHIP_STRING,
      objectDescription: true,
    },
    {
      header: "CurrentAccounts.partyToken",
      field: "party.token",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "CurrentAccounts.partyIsoCountryCode",
      field: "party.isoCountryCode",
      fieldType: FieldType.COUNTRY,
    }
  ];

  constructor(
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private currentAccountsService: CurrentAccountsService,
    private translate: TranslateService,
    private titleService: Title,
    private tableConfigService: TableConfigService
  ) {}

  ngOnInit(): void {
    this.menus$ = this.store.pipe(select(loadedMenusState));

    this.loadScreenTitle();
    this.getTableConfig();
    this.getCurrentAccountsList();
  }

  loadScreenTitle() {
    this.menuSub = this.menus$.subscribe((menus: MenuDTO[]) => {
      if (menus && menus.length) {
        this.route.data.subscribe((routeData: RouteData) => {
          menus.forEach((menuEl: MenuDTO) => {
            const selectedMenu = subMenusIterator(menuEl, routeData);
            if (selectedMenu) {
              this.headerIcon = selectedMenu.icon;
              this.tableTitle = selectedMenu.description;
              this.titleService.setTitle(this.tableTitle);
            }
          });
        });
      }
    });
  }

  getCurrentAccountsList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.isLoading$.next(true);
    this.getCurrentAccountsListSub = this.currentAccountsService
      .getCurrentAccountsList(
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.PAGE_SIZE,
          realSize: true,
        },
        searchEvent,
        sortEvent,
        filterEvent
      )
      .pipe(
        map((result) => {
          const { items, size } = result;
          this.totalCurrentAccounts$.next(size);
          const treatedItems = items.map((item: CurrentAccountDTO) => this.setCustomFields(item));
          return treatedItems;
        }),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("CurrentAccounts.currentAccountsLoadError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe((result: CurrentAccountDTO[]) => (this.tableData = result));
  }

  getCurrentAccountsExcelList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.isLoading$.next(true);
    this.excelCurrentAccountsList$ = this.currentAccountsService
      .getCurrentAccountsList(
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.EXCEL_PAGE_SIZE,
        },
        searchEvent,
        sortEvent,
        filterEvent
      )
      .pipe(
        map(({ items }) => items),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("CurrentAccounts.currentAccountsLoadError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      );
  }

  setCustomFields(currentAccount: CurrentAccountDTO) {
    let styleSettings: { style: string, icon: string };
    switch (currentAccount.partyRole.code) {
      case 'SUPPLIER':
        styleSettings = getChipStyle(CHIP_STYLE.WARNING);
        styleSettings.icon = 'local_shipping';
        break;
      case 'CUSTOMER':
        styleSettings = getChipStyle(CHIP_STYLE.SUCCESS);
        styleSettings.icon = 'shopping_cart';
        break;
      default:
        styleSettings = getChipStyle(CHIP_STYLE.INACTIVE);
        styleSettings.icon = 'person';
        break;
    }
    return {
      ...currentAccount,
       party: {
        ...currentAccount.party,
         isoCountryCode: {
           code: currentAccount.party.isoCountryCode,
           alpha2Code: currentAccount.party.alpha2CountryCode,
         },
       },
      partyRole: {
        ...currentAccount.partyRole,
        description: {
          value: currentAccount.partyRole.description[this.locale],
          icon: styleSettings.icon,
          style: styleSettings.style,
        }
      }
    };
  }

  getTableConfig() {
    this.columnInfo$ = this.tableConfigService.getConfigSettings(USAGE_CODE, ConfigSettingType.TABLE_CONFIG).pipe(
      map((response) => <ColumnSorterInfo[]>response.result?.value?.tableColumnInfo),
      catchError((error) => {
        this.store.dispatch(
          setSnackbar(SnackBarUtils.setSnackBar(this.translate.instant("TableConfig.LoadError"), SnackbarType.ERROR))
        );
        return EMPTY;
      }),
      finalize(() => this.isLoading$.next(false))
    );
  }

  onGetExportData(exportEvent: ExportEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = exportEvent;
    this.getCurrentAccountsExcelList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.isLoading$.next(true);
    this.tableConfigService
      .saveConfigSettings({
        code: USAGE_CODE,
        settingTypeCode: ConfigSettingType.TABLE_CONFIG,
        scope: ConfigScope.USER,
        value: { tableColumnInfo },
      })
      .pipe(
        tap(() =>
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(this.translate.instant("TableConfig.SaveSuccess"), SnackbarType.SUCCESS)
            )
          )
        ),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(SnackBarUtils.setSnackBar(this.translate.instant("TableConfig.SaveError"), SnackbarType.ERROR))
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }

  onGetTableData(tableEvent: TableEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    this.getCurrentAccountsList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  ngOnDestroy() {
    if (this.menuSub) {
      this.menuSub.unsubscribe();
    }
    if (this.getCurrentAccountsListSub) {
      this.getCurrentAccountsListSub.unsubscribe();
    }
  }
}
